import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { MessageCircle, ArrowLeft, Send, Loader2, Phone, RefreshCw, UserCheck, UserX, TrendingUp } from 'lucide-react';

/**
 * Chat Inbox — surfaces threads from **unknown** patients (lead-gen funnel).
 * Threads from known patients (matched by phone-hash against the visits table)
 * automatically merge into the Patient Log timeline as `source_kind="chat"`
 * entries and are intentionally NOT shown here.
 */
export default function ChatInboxTab() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [stats, setStats] = useState(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api.get('/dashboard/chats', { params: { inbox: 'new' } });
      setThreads(r.data.threads || []);
      setUnreadTotal(r.data.unread_total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/dashboard/chat-inbox/stats', { params: { window_days: 30 } });
      setStats(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadThreads(); loadStats(); }, [loadThreads, loadStats]);
  // Light polling so doctors get fresh leads without a manual refresh.
  useEffect(() => {
    const id = setInterval(loadThreads, 10000);
    return () => clearInterval(id);
  }, [loadThreads]);

  if (selectedId) {
    return (
      <ChatThreadView
        threadId={selectedId}
        onBack={() => { setSelectedId(null); loadThreads(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="chat-inbox-tab">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle size={20} className="text-sky-600" />
            Chat Inbox
            {unreadTotal > 0 && (
              <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-[11px] font-bold text-white bg-red-500 rounded-full" data-testid="chat-inbox-unread-pill">
                {unreadTotal}
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            New chats from people who aren't yet in your Patient Log. Existing patients' chats merge directly into <b>Patient Log</b>.
          </p>
        </div>
        <button onClick={() => { loadThreads(); loadStats(); }} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700" data-testid="chat-inbox-refresh-btn">
          <RefreshCw size={13} /> Refresh
        </button>
      </header>

      {stats && stats.leads > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 flex items-center gap-3" data-testid="chat-inbox-stats-banner">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <TrendingUp size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-900" data-testid="chat-inbox-stats-headline">{stats.headline}</p>
            <p className="text-[11px] text-emerald-800 mt-0.5">
              <b>{stats.converted}</b> of <b>{stats.leads}</b> new chats turned into patients in the last 30 days · <b>{stats.conversion_rate}%</b> conversion
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="py-12 text-center text-sm text-slate-400">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && threads.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center" data-testid="chat-inbox-empty">
          <MessageCircle size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-medium text-slate-700">No new chats yet</p>
          <p className="text-xs text-slate-500 mt-1">When a new patient (or relative) starts a chat from your public profile, they'll land here after mobile verification.</p>
        </div>
      )}

      {!loading && threads.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="chat-inbox-list">
          {threads.map(t => (
            <li key={t.id}>
              <button onClick={() => setSelectedId(t.id)} className="w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition" data-testid={`chat-thread-row-${t.id}`}>
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold text-sm shrink-0">
                  {(t.patient_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-slate-900 truncate">{t.patient_name || 'Unnamed'}</p>
                    {t.role === 'relative' && (
                      <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-bold">via relative</span>
                    )}
                    <span className="text-[10px] text-slate-400 ml-auto shrink-0">{formatWhen(t.last_at)}</span>
                  </div>
                  <p className="text-xs text-slate-600 truncate mt-0.5">{t.last_message_preview || '—'}</p>
                  <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5">
                    <Phone size={10} /> {t.patient_phone_masked}
                    <span className="text-slate-300">•</span>
                    <span>{t.message_count} {t.message_count === 1 ? 'message' : 'messages'}</span>
                  </p>
                </div>
                {t.unread_for_doctor > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid={`chat-thread-unread-${t.id}`}>
                    {t.unread_for_doctor}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChatThreadView({ threadId, onBack }) {
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/dashboard/chats/${threadId}`);
      setThread(r.data);
    } catch (e) { console.error(e); }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const sendReply = async () => {
    const t = reply.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post(`/dashboard/chats/${threadId}/reply`, { text: t });
      setThread(prev => prev ? { ...prev, messages: [...(prev.messages || []), r.data.message] } : prev);
      setReply('');
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  if (!thread) {
    return (
      <div className="py-12 text-center text-sm text-slate-400">
        <Loader2 size={20} className="animate-spin inline mr-2" /> Loading thread…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="chat-thread-view">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="chat-thread-back-btn">
        <ArrowLeft size={14} /> Back to inbox
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
          <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold text-sm">
            {(thread.patient_name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900">
              {thread.patient_name || 'Unnamed'}
              {thread.role === 'relative' && (
                <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-bold">Relative is messaging</span>
              )}
              {thread.is_known_patient ? (
                <span className="ml-2 text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1"><UserCheck size={10} /> existing</span>
              ) : (
                <span className="ml-2 text-[10px] uppercase tracking-wide bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1"><UserX size={10} /> new lead</span>
              )}
            </p>
            <p className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-2" data-testid="chat-thread-phone">
              <span className="inline-flex items-center gap-1"><Phone size={11} /> Patient: <b className="text-slate-900">+91 {thread.patient_phone || '—'}</b></span>
              {thread.relative_phone && (
                <span className="inline-flex items-center gap-1 text-amber-700">· Relative: <b>+91 {thread.relative_phone}</b></span>
              )}
            </p>
          </div>
          <a href={`tel:+91${thread.patient_phone}`} className="text-xs font-semibold text-sky-700 hover:text-sky-900 px-2 py-1 rounded hover:bg-sky-50 inline-flex items-center gap-1" data-testid="chat-thread-call-btn">
            <Phone size={12} /> Call
          </a>
        </header>

        <div ref={scrollRef} className="overflow-y-auto px-4 py-4 space-y-2.5" style={{ maxHeight: 480, minHeight: 280 }} data-testid="chat-thread-messages">
          {(thread.messages || []).length === 0 && (
            <p className="text-xs text-center text-slate-400 py-6">No messages yet.</p>
          )}
          {(thread.messages || []).map(m => (
            <div key={m.id} className={`flex ${m.sender === 'doctor' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${m.sender === 'doctor' ? 'bg-sky-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
                {m.text}
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${m.sender === 'doctor' ? 'text-white/70 justify-end' : 'text-slate-400'}`}>
                  <span>{new Date(m.at).toLocaleString([], { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
                  {/* Read receipts on doctor's own replies — patient's
                      thread poll marks them read_at on next refresh (≤5 s). */}
                  {m.sender === 'doctor' && (
                    m.read_at ? (
                      <span title={`Seen ${new Date(m.read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                            className="text-sky-200 font-bold tracking-tighter">✓✓</span>
                    ) : (
                      <span title="Sent" className="text-white/60 font-bold">✓</span>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-end gap-2 p-3 border-t border-slate-100 bg-slate-50/30">
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            rows={2}
            placeholder="Type your reply…"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-sky-500/20 outline-none"
            data-testid="chat-thread-reply-input"
          />
          <button onClick={sendReply} disabled={sending || !reply.trim()} className="rounded-full p-3 bg-sky-600 text-white disabled:opacity-50" data-testid="chat-thread-reply-send-btn">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMins = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffH = Math.round(diffMins / 60);
    if (diffH < 24) return `${diffH}h`;
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  } catch { return ''; }
}
